/* ====================================================================
   API CONFIG + AUTHENTICATED FETCH

   Importing this module installs the fetch wrapper that auto-injects the
   Bearer token from localStorage on every /api/ call. It must be imported
   before any component makes a request — App.js imports it at the top.

   Never add Authorization headers manually, and never use axios: it
   bypasses this wrapper. See COPILOT_GUIDE.md §5.
   ==================================================================== */

export const API = '';

export const PUBLIC_APP_ORIGIN = process.env.REACT_APP_PUBLIC_URL
  || (window.location.hostname === 'greensheetbowls.onrender.com' ? 'https://greensheetbowls.online' : window.location.origin);

const _origFetch = window.fetch.bind(window);
window.fetch = (url, opts = {}) => {
  const token = localStorage.getItem('greensheet_token');
  if (token && typeof url === 'string' && url.includes('/api/')) {
    const headers = new Headers(opts.headers || {});
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    opts = { ...opts, headers };
  }
  return _origFetch(url, opts);
};

/* ====================================================================
   ASKING THE API FOR SOMETHING, AND KNOWING WHEN IT SAID NO

   `fetch` does not reject on 403, 404 or 500 — it resolves with ok: false
   and an error object in the body. So this, written everywhere:

       fetch(url).then(r => r.json()).then(setTeams)

   treats a refusal as a success. `setTeams({ error: 'Not your club' })`
   puts an object where the screen expects an array, the next render calls
   teams.map, and React unmounts the whole app to a blank page — with no
   message, because from the code's point of view nothing went wrong.

   The same shape hides quieter failures too: setCompPlayers of an error
   object leaves a list that silently empties, and a PUT nobody checked
   reports "Saved!" over the top of a 403.

   Use these instead of bare fetch. They throw when the server says no, so
   one catch shows the person a reason.
   ==================================================================== */

/** Does this body look like a web page rather than data? */
const looksLikeHtml = body => /^\s*<(!doctype|html|\?xml)/i.test(body);

/* An unknown path under /api is answered in production by the single-page
   catch-all — with index.html and a status of 200. So a mistyped or
   withdrawn endpoint is not an error status at all: it is a success whose
   body is a web page, which is why this was reaching people as
   "Unexpected token '<'". It has to be checked on the happy path too, not
   only when the status is bad. */
const HTML_INSTEAD_OF_DATA = 'The server answered with a web page instead of data. '
  + 'The address this asked for may no longer exist.';

/** What the server said went wrong, in its own words where it gave any. */
function reasonFor(body, status) {
  if (looksLikeHtml(body)) return `${HTML_INSTEAD_OF_DATA} (${status})`;
  try {
    const parsed = JSON.parse(body);
    if (parsed && parsed.error) return parsed.error;
  } catch { /* not JSON; fall through to the status */ }
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'That is no longer there.';
  return `The server could not do that (${status}).`;
}

/** An Error carrying the status, so a caller can treat 403 differently. */
function apiError(message, status) {
  const err = new Error(message);
  err.status = status;
  return err;
}

/**
 * Call the API and return the parsed body, or throw with a reason.
 *
 * `body` is serialised for you and sets the Content-Type, because forgetting
 * that header is how a PUT ends up with an empty req.body on the server.
 */
export async function apiJson(url, { method = 'GET', body, ...rest } = {}) {
  let res;
  try {
    res = await fetch(`${API}${url}`, {
      method,
      ...(body === undefined ? {} : {
        headers: { 'Content-Type': 'application/json', ...(rest.headers || {}) },
        body: JSON.stringify(body),
      }),
      ...rest,
    });
  } catch {
    // No response at all: offline, server down, DNS.
    throw apiError('Could not reach the server. Check your connection.', 0);
  }

  /* Read as text once, then decide. Reading it twice is not allowed, and the
     text is needed either way — to find the server's own message when it
     refused, and to notice a web page arriving where data was expected. */
  const answer = await res.text().catch(() => '');
  if (!res.ok) throw apiError(reasonFor(answer, res.status), res.status);
  if (res.status === 204 || answer.trim() === '') return null;
  if (looksLikeHtml(answer)) throw apiError(`${HTML_INSTEAD_OF_DATA} (${res.status})`, res.status);
  try {
    return JSON.parse(answer);
  } catch {
    throw apiError('The server sent something this could not read.', res.status);
  }
}

/**
 * The same, for an endpoint that returns a list.
 *
 * Guarantees an array or throws. The crash this exists to prevent is not a
 * failed request — it is a successful-looking one whose body is not the
 * shape the screen is about to iterate.
 */
export async function apiList(url, opts) {
  const data = await apiJson(url, opts);
  if (!Array.isArray(data)) {
    throw apiError('The server sent something other than a list.', 200);
  }
  return data;
}
